import { z } from "zod";
import type { Locale } from "./common";
import type { SubscriptionPlan } from "./subscription-plan";

// TES-1459 (TES-1458 §1, Emelie 2026-10-20) — the one shared price overview,
// "Vad kostar mötet och behandlingen?". The decision: "Återanvänd exakt samma
// pristext från en gemensam källa i: återkopplingens befintliga prisdropdown,
// bokningsflödet före betalning, bokningskortet på Hem." This module is that
// source. F-01's result builds its cost section from it; the booking flow
// (TES-1460) and the Hem booking card (TES-1461) import it too, and none of
// them keeps a local copy.
//
// Every Swedish string is verbatim from the F-01 doc's "Expanderbar sektion 3
// — Gemensam prisdropdown — patienttext" (Drive 1O85MBLolKJsm_NCG9ddO2ZQvUMHsRgbRNlH1B9jKRpM,
// 2026-10-20), with the amounts interpolated from the tenant config and the
// API's CONSULT_FEE_CENTS, never hardcoded. The doc's "Intern
// kompletteringspunkt" is not patient copy and is not here. The English is
// ours (proposed, not approved).
//
// One deliberate exception to "verbatim": the doc says "Inga lanseringskoder
// eller gratisperioder ska visas", but the checkout still grants a free first
// month while SUBSCRIPTION_FREE_MONTHS > 0 (TES-809; whether it keeps doing
// so is Ö-818, TES-1462). Saying nothing about a month she will in fact not
// be charged for would misstate her price, so while the offer is configured
// the Uppstart block ends with the two previously approved free-month lines
// (TES-770 web copy and Ö-359). At 0 they disappear and the overview is the
// doc's text exactly.
//
// TES-1462 (Ö-818, Emelie 2026-10-21): the offer stays, and it moves out of
// the regular price list: "Behåll ordinarie prislista separat och gemensam
// centralt styrd kampanjruta efter F-01, på Hem under Att göra, överst i
// prisdropdownen och före betalning." `campaignBox` is that one box, and
// SUBSCRIPTION_FREE_MONTHS is its central switch, the same value the
// checkout's trial reads. With `separateCampaign` the overview carries the
// box as `campaign` and the regular blocks name no offer. Without it (any
// client that does not claim CLIENT_CONTRACT_CHANGES.campaignBox, and the
// web, which Ö-818 leaves unchanged) the lines end Uppstart as before.
//
// TES-1462 (Ö-818, Ö-932 a, Simon 2026-10-26) — a prescription transfer
// patient: "Receptövertag: gratis första månad; först när abonnemanget är
// känt visas 995 kr för Uppstart eller 649 kr för Underhåll som rabatt." The
// free month stays the checkout's Stripe trial (no coupon), so the "rabatt"
// is a line of text: once her plan is known, the box ends with what the free
// month saves her, that plan's monthly price from the tenant config
// (`transferSavingMinor`). Never a hardcoded amount, so Ö-942 (which price
// the maintenance plan has) is settled in the config alone: 695 SEK since
// TES-1599, although the quoted decision says 649. The line is
// proposed copy that Emelie has not approved yet.

/** One headed block of the overview: a heading line, then its body. */
export const priceOverviewBlockSchema = z.object({
  heading: z.string(),
  paragraphs: z.array(z.string()),
});
export type PriceOverviewBlock = z.infer<typeof priceOverviewBlockSchema>;

/**
 * The whole overview: its title (the dropdown's closed label) and the
 * headed blocks. TES-1461 — the schema is what Hem's booking card carries.
 */
export const priceOverviewSchema = z.object({
  title: z.string(),
  blocks: z.array(priceOverviewBlockSchema),
  /**
   * TES-1462 — the campaign box, shown at the head of the dropdown and
   * visible while it is closed. Absent while no campaign runs, and for a
   * client that does not claim CLIENT_CONTRACT_CHANGES.campaignBox.
   */
  campaign: priceOverviewBlockSchema.optional(),
});
export type PriceOverview = z.infer<typeof priceOverviewSchema>;

/** The amounts the overview names, in minor units (hundredths of a krona). */
export interface PriceOverviewAmounts {
  /** The digital care meeting: the API's CONSULT_FEE_CENTS. */
  consultMinor: number;
  /** Ella Uppstart, from the tenant config's `pricing`. */
  coreSubscriptionMinor: number;
  /**
   * Ella Underhåll, from the tenant config's `pricing.maintenanceMinor`.
   * Optional there, so without it the maintenance block is left out rather
   * than given a hardcoded price.
   */
  maintenanceMinor?: number;
  /** A new blood test, from the tenant config's `pricing`. */
  labTestMinor: number;
  /**
   * Months free at sign-up while the launch offer runs: the API's
   * SUBSCRIPTION_FREE_MONTHS. Above 0, the Uppstart block says so (see the
   * note at the top); omitted or 0, it does not.
   */
  launchOfferFreeMonths?: number;
  /**
   * TES-1462 (Ö-932) — set only for a prescription transfer patient whose
   * plan is known: that plan's monthly price (`planMonthlyMinor`). The
   * campaign box then ends with what the free month saves her. Ignored
   * while no campaign runs, and without `separateCampaign`.
   */
  transferSavingMinor?: number;
}

/**
 * TES-1462 — a plan's monthly price from the tenant config's `pricing`, or
 * null when that plan has no price configured (never another plan's price).
 */
export function planMonthlyMinor(
  plan: SubscriptionPlan,
  pricing: { coreSubscriptionMinor: number; maintenanceMinor?: number },
): number | null {
  return plan === "ella_underhall"
    ? (pricing.maintenanceMinor ?? null)
    : pricing.coreSubscriptionMinor;
}

/**
 * A price in minor units the way patient copy shows it: "395 kr" or
 * "SEK 395". Whole kronor, formatted like the web's `formatKr`.
 */
export function formatKronor(minor: number, locale: Locale): string {
  const digits = new Intl.NumberFormat(locale === "en" ? "en-GB" : "sv-SE", {
    maximumFractionDigits: 0,
  }).format(Math.round(minor / 100));
  return locale === "en" ? `SEK ${digits}` : `${digits} kr`;
}

interface Amounts {
  consult: string;
  core: string;
  maintenance: string | null;
  lab: string;
}

const COPY: Record<
  "sv" | "en",
  {
    title: string;
    campaignHeading: string;
    campaignLead: (free: string) => string;
    transferSaving: (saved: string) => string;
    meeting: (a: Amounts) => PriceOverviewBlock;
    uppstart: (a: Amounts) => PriceOverviewBlock;
    freeMonth: string[];
    underhall: (maintenance: string) => PriceOverviewBlock;
    binding: PriceOverviewBlock;
    medication: PriceOverviewBlock;
    lab: (a: Amounts) => PriceOverviewBlock;
    reschedule: PriceOverviewBlock;
  }
> = {
  sv: {
    title: "Vad kostar mötet och behandlingen?",
    // TES-1462 — the box reuses approved copy only: the web's offer tag and
    // title (TES-770, apps/web sv.json `landing.price`), then the Ö-359 line.
    campaignHeading: "Lanseringserbjudande",
    campaignLead: (free) => `${free} i månadsavgift första månaden.`,
    // TES-1462 (Ö-932) — proposed copy, not yet approved by Emelie.
    transferSaving: (saved) =>
      `Första månaden är kostnadsfri – du sparar ${saved}.`,
    meeting: (a) => ({
      heading: `Ditt första vårdmöte — ${a.consult}`,
      paragraphs: [
        "Du betalar för ett digitalt möte med läkaren och en individuell medicinsk bedömning. Att boka mötet startar inget abonnemang.",
      ],
    }),
    uppstart: (a) => ({
      heading: `Ella Uppstart — ${a.core} per månad`,
      paragraphs: [
        "Om läkaren godkänner behandling väljer du själv om du vill gå vidare.",
        "I Ella Uppstart ingår medicinsk uppföljning, receptförnyelse efter läkarens bedömning och personlig coach för vanor, motivation och känslor kring din viktresa. Du får också tillgång till incheckningar, kurser, verktyg, artiklar och möjlighet att följa din utveckling i appen.",
      ],
    }),
    freeMonth: [
      "Du betalar ingen månadsavgift under din första månad.",
      "Den kostnadsfria första månaden börjar när du tecknar abonnemanget.",
    ],
    underhall: (maintenance) => ({
      heading: `Ella Underhåll — ${maintenance} per månad`,
      paragraphs: [
        "För dig som har minst tolv månaders verifierad medicinskt övervakad behandling med viktläkemedel och inte längre behöver personlig coach. Verifierad behandling hos en annan vårdgivare kan räknas med.",
        "Du får fortsatt medicinsk uppföljning, receptförnyelse efter läkarens bedömning och tillgång till incheckningar, kurser, verktyg, artiklar och din hälsoutveckling i appen. Personlig coach ingår inte. Läkaren verifierar behandlingshistoriken och du väljer själv om du vill gå över till Ella Underhåll.",
      ],
    }),
    binding: {
      heading: "Bindningstid och uppsägning",
      paragraphs: [
        "Abonnemanget betalas månadsvis och har tre månaders bindningstid. Därefter gäller en månads uppsägningstid.",
      ],
    },
    medication: {
      heading: "Läkemedel betalas separat",
      paragraphs: [
        "Läkemedlet ingår inte i abonnemanget. Du hämtar ut och betalar det på apotek. Kostnaden varierar beroende på vilket läkemedel och vilken dos läkaren ordinerar samt apotekets pris.",
      ],
    },
    lab: (a) => ({
      heading: `Blodprov — ${a.lab} vid behov`,
      paragraphs: [
        `Om läkaren bedömer att du behöver lämna ett nytt blodprov kostar det ${a.lab}. Kostnaden är separat från vårdmötet, abonnemanget och läkemedlet. Om läkaren kan använda tidigare provsvar behöver du inte betala för en ny provtagning.`,
      ],
    }),
    reschedule: {
      heading: "Om du behöver ändra din mötestid",
      paragraphs: [
        "Du kan avboka eller boka om kostnadsfritt senast 24 timmar före mötet. Vid senare ändring eller uteblivet besök återbetalas inte mötesavgiften. Om Ella ställer in mötet får du avgiften tillbaka.",
      ],
    },
  },
  en: {
    title: "What do the meeting and the treatment cost?",
    // The web's en.json wording of the same offer.
    campaignHeading: "Launch offer",
    campaignLead: (free) => `${free} monthly fee for the first month.`,
    transferSaving: (saved) => `Your first month is free – you save ${saved}.`,
    meeting: (a) => ({
      heading: `Your first consultation — ${a.consult}`,
      paragraphs: [
        "You pay for a digital meeting with the doctor and an individual medical assessment. Booking the meeting does not start a subscription.",
      ],
    }),
    uppstart: (a) => ({
      heading: `Ella Start — ${a.core} per month`,
      paragraphs: [
        "If the doctor approves treatment, you choose for yourself whether to go ahead.",
        "Ella Start includes medical follow-up, prescription renewal at the doctor's discretion and a personal coach for habits, motivation and feelings around your weight journey. You also get check-ins, courses, tools, articles and the option to follow your progress in the app.",
      ],
    }),
    freeMonth: [
      "You pay no monthly fee during your first month.",
      "The free first month starts when you take out the subscription.",
    ],
    underhall: (maintenance) => ({
      heading: `Ella Maintenance — ${maintenance} per month`,
      paragraphs: [
        "For you who have at least twelve months of verified, medically supervised treatment with weight medication and no longer need a personal coach. Verified treatment with another care provider can count.",
        "You keep medical follow-up, prescription renewal at the doctor's discretion and access to check-ins, courses, tools, articles and your health progress in the app. A personal coach is not included. The doctor verifies your treatment history and you choose for yourself whether to move to Ella Maintenance.",
      ],
    }),
    binding: {
      heading: "Minimum term and notice",
      paragraphs: [
        "The subscription is paid monthly and has a three-month minimum term. After that, one month's notice applies.",
      ],
    },
    medication: {
      heading: "Medication is paid for separately",
      paragraphs: [
        "The medication is not included in the subscription. You collect and pay for it at the pharmacy. The cost depends on which medication and dose the doctor prescribes and on the pharmacy's price.",
      ],
    },
    lab: (a) => ({
      heading: `Blood test — ${a.lab} if needed`,
      paragraphs: [
        `If the doctor decides you need to give a new blood sample, it costs ${a.lab}. The cost is separate from the consultation, the subscription and the medication. If the doctor can use earlier test results, you do not pay for a new sample.`,
      ],
    }),
    reschedule: {
      heading: "If you need to change your meeting time",
      paragraphs: [
        "You can cancel or rebook free of charge up to 24 hours before the meeting. For later changes or a missed meeting, the meeting fee is not refunded. If Ella cancels the meeting, you get the fee back.",
      ],
    },
  },
};

/**
 * TES-1462 (Ö-818) — the one campaign box, or null while no campaign runs
 * (`launchOfferFreeMonths` omitted or 0). Every surface that shows it gets
 * it from here, so changing SUBSCRIPTION_FREE_MONTHS changes all of them
 * together with the checkout. With `transferSavingMinor` (a transfer
 * patient whose plan is known, Ö-932) it ends with her saving.
 */
export function campaignBox(
  locale: Locale,
  launchOfferFreeMonths: number | undefined,
  transferSavingMinor?: number | null,
): PriceOverviewBlock | null {
  if ((launchOfferFreeMonths ?? 0) <= 0) return null;
  const lang = locale === "en" ? "en" : "sv";
  const copy = COPY[lang];
  const saving = transferSavingLine(
    lang,
    launchOfferFreeMonths,
    transferSavingMinor,
  );
  return {
    heading: copy.campaignHeading,
    paragraphs: [
      copy.campaignLead(formatKronor(0, lang)),
      copy.freeMonth[1],
      ...(saving ? [saving] : []),
    ],
  };
}

/**
 * TES-1462 (Ö-932) — "Första månaden är kostnadsfri – du sparar 995 kr.":
 * what the free month saves a prescription transfer patient on her known
 * plan. Null while no campaign runs or without a price, so it never names an
 * amount the checkout does not give.
 */
export function transferSavingLine(
  locale: Locale,
  launchOfferFreeMonths: number | undefined,
  transferSavingMinor: number | null | undefined,
): string | null {
  if ((launchOfferFreeMonths ?? 0) <= 0) return null;
  if (transferSavingMinor == null || transferSavingMinor <= 0) return null;
  const lang = locale === "en" ? "en" : "sv";
  return COPY[lang].transferSaving(formatKronor(transferSavingMinor, lang));
}

/**
 * The shared price overview, in the doc's order: the meeting, Ella Uppstart,
 * Ella Underhåll, the minimum term, medication, the blood test, and changing
 * the meeting time. With `separateCampaign` (TES-1462) a running campaign is
 * the separate `campaign` box and the blocks are the regular price list
 * only; without it the free-month lines end the Uppstart block.
 */
export function priceOverview(
  locale: Locale,
  amounts: PriceOverviewAmounts,
  options: { separateCampaign?: boolean } = {},
): PriceOverview {
  const lang = locale === "en" ? "en" : "sv";
  const copy = COPY[lang];
  const a: Amounts = {
    consult: formatKronor(amounts.consultMinor, lang),
    core: formatKronor(amounts.coreSubscriptionMinor, lang),
    maintenance:
      amounts.maintenanceMinor === undefined
        ? null
        : formatKronor(amounts.maintenanceMinor, lang),
    lab: formatKronor(amounts.labTestMinor, lang),
  };
  const uppstart = copy.uppstart(a);
  const campaign = options.separateCampaign
    ? campaignBox(
        lang,
        amounts.launchOfferFreeMonths,
        amounts.transferSavingMinor,
      )
    : null;
  if (!options.separateCampaign && (amounts.launchOfferFreeMonths ?? 0) > 0) {
    uppstart.paragraphs.push(...copy.freeMonth);
  }
  const blocks = [
    copy.meeting(a),
    uppstart,
    ...(a.maintenance ? [copy.underhall(a.maintenance)] : []),
    copy.binding,
    copy.medication,
    copy.lab(a),
    copy.reschedule,
  ];
  // Fresh copies, so a caller that edits its result cannot change the copy.
  return {
    title: copy.title,
    blocks: blocks.map((b) => ({
      heading: b.heading,
      paragraphs: [...b.paragraphs],
    })),
    // campaignBox builds a fresh box on every call.
    ...(campaign ? { campaign } : {}),
  };
}

/**
 * The blocks as plain paragraphs, each heading as a line of its own before
 * its body: for a client that renders only paragraphs (app ≤ 2.0.11), so it
 * still shows the full text in the same order.
 */
export function flattenPriceOverview(blocks: PriceOverviewBlock[]): string[] {
  return blocks.flatMap((b) => [b.heading, ...b.paragraphs]);
}
